import { useEffect } from 'react';

/**
 * Injects a `<script type="application/ld+json">` block with a stable id so
 * the same slot can be updated when props change and cleaned up on unmount.
 *
 * Pass a plain JS object as `data` — it will be JSON.stringify'd. Pass a
 * falsy value to remove the previously injected block for this id.
 */
export function useJsonLd(id, data) {
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const tagId = `jsonld-${id}`;
        // If data is falsy, remove any existing tag for this id
        if (!data) {
            const existing = document.getElementById(tagId);
            if (existing) existing.remove();
            return undefined;
        }
        let node = document.getElementById(tagId);
        if (!node) {
            node = document.createElement('script');
            node.type = 'application/ld+json';
            node.id = tagId;
            document.head.appendChild(node);
        }
        try {
            node.textContent = JSON.stringify(data);
        } catch (e) {
            /* ignore serialisation errors */
        }
        return () => {
            const el = document.getElementById(tagId);
            if (el) el.remove();
        };
    // We deliberately serialise data to detect deep changes without listing
    // every possible field as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, JSON.stringify(data)]);
}

// Build a BreadcrumbList schema from an array of {name, url}
export function breadcrumbSchema(items) {
    if (!items || items.length === 0) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((it, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: it.name,
            item: it.url,
        })),
    };
}

// Origin helper — safe in SSR
export function siteOrigin() {
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }
    return 'https://www.digitechcloud-ai.com';
}
